import React, { FC, useEffect, useState } from 'react';
import Yup from '~yup';

import UtilFunctions from '~utils/functions';

import Text from '~components/atoms/Text';
import { TCreateCollateralReduxParams, TCustomerInventory } from '~types/feat/customer';
import { useFormik } from 'formik';
import LabelledInput from '~components/atoms/LabelledInput';
import LabelledDropdown from '~components/atoms/LabelledDropdown';
import { CustomerAccFundTypeEnum, CustomerAccTypeEnum, FundTypeEnum, GoldTypeEnum, MetalTypeEnum, SilverTypeEnum } from '~utils/enums';
import { useAppDispatch, useAppSelector } from '~redux/hooks';
import { TDropdownOption } from '~types/components/labelledDropdown';

import combinedActions from '~redux/combinedActions';
import { TInventoryFilter } from '~types/feat/gold';
import ActionButton from '~components/atoms/ActionButton';
import Checkbox from '~components/atoms/Checkbox';
import LabelledDatepicker from '~components/atoms/LabelledDatepicker';
import dayjs from 'dayjs';
import { initialState as customerInitialState } from '~redux/slices/customer';

import * as FormStyles from './_customerManagementCreateCollateralForm.module.scss';

type TCustomerManagementCreateCollateralForm = {
    submitButtonLabel: string;
    onSubmit?: (data: TCreateCollateralReduxParams) => void;
    error: string;
    inputIsDisabled?: boolean;
    actionButtonIsLoading?: boolean;
    actionButtonClassName?: string;
    actionButtonIsDisabled?: boolean;
};

const CustomerManagementCreateCollateralForm: FC<TCustomerManagementCreateCollateralForm> = ({
    submitButtonLabel,
    onSubmit,
    error,
    inputIsDisabled,
    actionButtonIsLoading,
    actionButtonClassName,
    actionButtonIsDisabled,
}) => {
    const dispatch = useAppDispatch();
    const { Misc: { blockEnterKey } } = UtilFunctions;

    const customerList = useAppSelector((state) => state.customer.allCustomers);
    const customerDetails = useAppSelector((state) => state.customer.customerDetails);
    const searchCustomerIsLoading = useAppSelector(state => state.customer.loadings.getAllCustomers);
    const inventoryList = useAppSelector((state) => state.gold.allInventories);
    const customerInventory = useAppSelector((state) => state.customer.customerInventory);
    const marketValue = useAppSelector((state) => state.customer.createCollateralParams.marketValue);
    const isCreateCollateralFormFilled = useAppSelector((state) => state.customer.modals.isCreateCollateralFormFilled);
    const createCollateralParams = useAppSelector((state) => state.customer.createCollateralParams);
    const goldPrices = useAppSelector((state) => state.gold.buyAndSellGold.goldPrices);
    const silverPrices = useAppSelector((state) => state.silver.buyAndSellSilver.silverPrices);
    const inventoryFilterParams = useAppSelector((state) => state.gold.params.inventoryFilterParams);

    const createCollateralInitialState = customerInitialState.createCollateralParams;

    const [name, setName] = useState('');
    const [disableCustomerSearch, setDisableCustomerSearch] = useState(false);
    const [gettingCustomers, setGettingCustomers] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<TInventoryFilter>();
    const [selectedCustomerInventory, setSelectedInventory] = useState<TCustomerInventory>();
    const [customerBalance, setCustomerBalance] = useState('0');
    const [useApi, setUseApi] = useState(false);
    const selectedMetalType = useAppSelector((state) => state.customer.selectedCollateralMetalType);

    const customerOptions = customerList.map((customer) => ({
        label: `${customer.name} - ${customer.idNo}  (${CustomerAccFundTypeEnum[customer.fundType]}) (${CustomerAccTypeEnum[customer.accountType]})${customer.accountType === CustomerAccTypeEnum.Joint ? (' - ' + customer.childName) : ''}`,
        value: customer.customerId,
    }));

    const CustomerManagementCreateCustomerFormValidation = Yup.object().shape({
        customerId: Yup.number().required('Customer is required'),
        productId: Yup.number().required('Product is required'),
        productName: Yup.string().required('Product is required'),
        amount: Yup.number().typeError('Value must be a number')
            .test('is-within-balance',
                'Quantity must not exceed available balance',
                function (value) {
                    if (!value) return false;
                    if (!selectedCustomerInventory) return false;
                    return value <= selectedCustomerInventory.amount;
                })
            .test('no-exponential-notation',
                'Quantity must be a whole number (exponential notation is not allowed)',
                function (value) {
                    const original = this.originalValue;
                    if (original === undefined || original === null || original === '') return true;
                    return !/e/i.test(String(original));
                }
            )
            .min(0, 'Value cannot be negative').required('Amount is required'),
        goldPrice: Yup.number().min(0, 'Value cannot be negative').typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;
                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            })
            .when('metalType', {
                is: (metalType: string) => metalType !== 'Silver' && !useApi,
                then: (schema) => schema.required('Gold Price is required'),
                otherwise: (schema) => schema.notRequired().nullable(),
            })
            .test('no-exponential-notation',
                'Gold Price must be a whole number (exponential notation is not allowed)',
                function (value) {
                    const original = this.originalValue;
                    if (original === undefined || original === null || original === '') return true;
                    return !/e/i.test(String(original));
                }
            ),
        silverPrice: Yup.number().min(0, 'Value cannot be negative').typeError('Value must be a number')
            .test('no-space', 'Spaces are not allowed', (value, context) => {
                const rawValue = context.originalValue;
                if (typeof rawValue === 'string' && /\s/.test(rawValue)) {
                    return false;
                }
                return true;
            })
            .when('metalType', {
                is: (metalType: string) => metalType === 'Silver' && !useApi,
                then: (schema) => schema.required('Silver Price is required'),
                otherwise: (schema) => schema.notRequired().nullable(),
            })
            .test('no-exponential-notation',
                'Silver Price must be a whole number (exponential notation is not allowed)',
                function (value) {
                    const original = this.originalValue;
                    if (original === undefined || original === null || original === '') return true;
                    return !/e/i.test(String(original));
                }
            ),
        metalType: Yup.string().required('Metal Type is required'),
        collateralEndDate: Yup.string().required('Collateral Expiry Date is required'),
        remarks: Yup.string(),
    });

    const createCollateralFormInitialValues = {
        customerId: '',
        productId: '',
        productName: '',
        metalType: '',
        amount: '',
        goldPrice: '',
        silverPrice: '',
        collateralEndDate: '',
        remarks: '',
    };

    const formik = useFormik({
        initialValues: createCollateralFormInitialValues,
        validationSchema: CustomerManagementCreateCustomerFormValidation,
        onSubmit: (values) => {
            const {
                customerId,
                productId,
                amount,
                goldPrice,
                silverPrice,
                remarks,
                collateralEndDate,
            } = values;

            const isSilver = selectedMetalType === MetalTypeEnum.Silver;
            const enteredPrice = Number(isSilver ? silverPrice : goldPrice);

            dispatch(combinedActions.customerSetCreateCollateralParams({
                customerId: Number(customerId),
                productId: Number(productId),
                productName: selectedProduct?.productName,
                amount: Number(amount),
                goldPrice: useApi || isSilver ? undefined : enteredPrice,
                silverPrice: useApi || !isSilver ? undefined : enteredPrice,
                remarks: remarks || undefined,
                collateralEndDate,
                marketValue,
            }));
            dispatch(combinedActions.customerSetIsCreateCollateralFormFilled(true));
        },
    });

    const availableInventoryOptions = inventoryList.filter(item => {
        if (formik.values.metalType === 'Silver') {
            return item.silverType === SilverTypeEnum.PaperSilver;
        }
        return item.goldType === GoldTypeEnum.PaperGold;
    });

    // useEffects

    useEffect(() => {
        if (!isCreateCollateralFormFilled && customerDetails.customerId !== 0){
            const { customerId, productId, productName, amount, goldPrice, silverPrice, remarks, collateralEndDate } = createCollateralParams;

            const isSilver = selectedMetalType === MetalTypeEnum.Silver;
            const restoredPrice = isSilver ? silverPrice : goldPrice;

            formik.setValues({
                ...formik.values,
                customerId: customerId.toString(),
                productId: productId.toString(),
                productName: productName || '',
                metalType: MetalTypeEnum[selectedMetalType],
                amount: amount.toString(),
                goldPrice: goldPrice?.toString() || '',
                silverPrice: silverPrice?.toString() || '',
                remarks: remarks?.toString() || '',
                collateralEndDate: collateralEndDate.toString(),
            });

            setUseApi(!restoredPrice);

            const restoredInventoryOptions = inventoryList.filter(item =>
                isSilver
                    ? item.silverType === SilverTypeEnum.PaperSilver
                    : item.goldType === GoldTypeEnum.PaperGold
            );

            const product = restoredInventoryOptions.find((inventory) => inventory.productId === productId);

            if (!product) return;

            setSelectedProduct(product);
            setName(`${customerDetails.name} - ${customerDetails.idNo}  (${CustomerAccFundTypeEnum[customerDetails.fundType]})`);
        }
    }, [isCreateCollateralFormFilled]);

    useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (name && !disableCustomerSearch) {
            setGettingCustomers(true);

            // debounce customer name input
            timeout = setTimeout(() => {
                dispatch(combinedActions.customerGetAllCustomersAttempt({ searchQuery: name }));
            }, 1000);
        } else setGettingCustomers(false);

        setDisableCustomerSearch(false);

        return () => {
            setGettingCustomers(false);
            clearTimeout(timeout);
        };
    }, [name]);

    useEffect(() => {
        // to set loading state (debouncing causes loading state to also delay hence we use a local loading state)
        if (!searchCustomerIsLoading) {
            setGettingCustomers(false);
        }
    }, [searchCustomerIsLoading]);

    useEffect(() => {
        if (Number(formik.values.customerId) > 0 && formik.values.metalType) {
            const isGold = formik.values.metalType === 'Gold';

            dispatch(combinedActions.customerGetCustomerInventoryAttempt({
                customerId: Number(formik.values.customerId),
                metalType: isGold ? MetalTypeEnum.Gold : MetalTypeEnum.Silver,
                ...(isGold
                    ? { goldType: GoldTypeEnum.PaperGold }
                    : { silverType: SilverTypeEnum.PaperSilver }
                ),
            }));
        }
    }, [selectedProduct, customerDetails, formik.values.metalType]);

    useEffect(() => {
        if (selectedProduct) {
            const selectedInventory = customerInventory.find((inventory) => inventory.productId === selectedProduct.productId);

            if (!selectedInventory) return;

            setSelectedInventory(selectedInventory);

            if (selectedProduct.goldType === GoldTypeEnum.PhysicalGold) {
                setCustomerBalance(`${selectedInventory.amount ? selectedInventory.amount.toString() : 0} pcs`);
            } else if (selectedProduct.goldType === GoldTypeEnum.PaperGold) {
                setCustomerBalance(`${selectedInventory.amount ? selectedInventory.amount.toFixed(3) : 0} g`);
            }

            if (selectedProduct.silverType === SilverTypeEnum.PhysicalSilver) {
                setCustomerBalance(`${selectedInventory.amount ? selectedInventory.amount.toString() : 0} pcs`);
            } else if (selectedProduct.silverType === SilverTypeEnum.PaperSilver) {
                setCustomerBalance(`${selectedInventory.amount ? selectedInventory.amount.toFixed(3) : 0} g`);
            }
        }
        else {
            setCustomerBalance('Please select customer first');

            if (customerInventory.length > 0){
                setCustomerBalance('Please select product first');
            }
        }
    }, [selectedProduct]);

    useEffect(() => {
        let timeout: NodeJS.Timeout;

        if (formik.values.amount !== '') {

            // debounce customer name input
            timeout = setTimeout(() => {
                dispatch(combinedActions.customerGetCollateralMarketValueAttempt({
                    amount: Number(formik.values.amount),
                    goldPrice: useApi ? undefined : Number(formik.values.goldPrice),
                    silverPrice: useApi ? undefined : Number(formik.values.silverPrice),
                    metalType: selectedMetalType,
                }));
            }, 500);
        }

        return () => {
            clearTimeout(timeout);
        };
    }, [formik.values.goldPrice, formik.values.silverPrice, formik.values.amount, formik.values.metalType, useApi]);

    useEffect(() => {
        dispatch(combinedActions.goldSetInventoryFilterParams({
            ...inventoryFilterParams,
            metalType: MetalTypeEnum.Gold,
            goldType: undefined,
            silverType: undefined,
        }));
        dispatch(combinedActions.goldGetAllInventoriesAttempt());

        dispatch(combinedActions.goldSetInventoryFilterParams({
            ...inventoryFilterParams,
            metalType: MetalTypeEnum.Silver,
            goldType: undefined,
            silverType: undefined,
        }));
        dispatch(combinedActions.goldGetAllInventoriesAttempt());
    }, []);

    useEffect(() => {
        if (!formik.values.metalType) return;

        dispatch(combinedActions.goldSetInventoryFilterParams({
            ...inventoryFilterParams,
            metalType: formik.values.metalType === 'Silver' ? MetalTypeEnum.Silver : MetalTypeEnum.Gold,
            goldType: undefined,
            silverType: undefined,
        }));
        dispatch(combinedActions.goldGetAllInventoriesAttempt());
    }, [formik.values.metalType]);

    // handler functions
    const handleCustomerSelected = (customerId: number) => {
        dispatch(combinedActions.customerGetCustomerDetailsAttempt({ customerId }));
    };

    const handleOnSearchCustomer = (event: React.ChangeEvent<HTMLInputElement>) => {
        setName(event.target.value);

        const customer = customerList.find((customerOption) => customerOption.idNo === event.target.value);

        if (!customer) return;

        formik.setValues({
            ...formik.values,
            customerId: customer.customerId.toString(),
        });
    };

    const handleOnSelectCustomer = (option: TDropdownOption) => {
        const customer = customerList.find((customerOption) => customerOption.customerId === option.value);

        if (!customer) return;

        setDisableCustomerSearch(true);

        handleCustomerSelected(customer.customerId);

        setName(`${customer.name} - ${customer.idNo}  (${CustomerAccFundTypeEnum[customer.fundType]})`);

        formik.setValues({
            ...formik.initialValues,
            customerId: customer.customerId.toString(),
        });
        formik.setTouched(formik.initialTouched);

        dispatch(combinedActions.customerSetCreateCollateralParams(createCollateralInitialState));

        setCustomerBalance('Please select product first');
        setSelectedProduct(undefined);
    };

    const handleOnSearchMetalType = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange({ target: { name: 'metalType', value: event.target.value } });
    };

    const handleOnSelectMetalType = (option: TDropdownOption) => {
        const metalType = MetalTypeEnum[option.label as keyof typeof MetalTypeEnum];

        dispatch(combinedActions.customerSetSelectedCollateralMetalType(metalType));

        formik.setFieldValue('metalType', option.label);
        formik.setFieldValue('goldPrice', '');
        formik.setFieldValue('silverPrice', '');
        formik.setFieldValue('productName', '');
    };

    const handleOnSearchProduct = (event: React.ChangeEvent<HTMLInputElement>) => {
        formik.handleChange({ target: { name: 'productName', value: event.target.value } });
    };

    const handleOnSelectProduct = (option: TDropdownOption) => {
        formik.setFieldValue('amount', '');

        const product = availableInventoryOptions.find((inventory) => inventory.productId === option.value);

        if (!product) return;

        setSelectedProduct(product);

        formik.setValues({
            ...formik.values,
            productId: product.productId.toString(),
            productName: product.productName,
        });
    };

    const inventoryOptions = () => {
        if (availableInventoryOptions.length > 0) {
            const filteredInventoryByFundType = availableInventoryOptions.filter(item => FundTypeEnum[item.fundType] === FundTypeEnum[customerDetails.fundType]);

            const filteredInventory = filteredInventoryByFundType.filter(item =>
                customerInventory.find(elem => elem.productId === item.productId)
            );

            return filteredInventory.map((inventory) => ({
                label: inventory.productName,
                value: inventory.productId,
            }));
        }

        return [];
    };

    const metalTypeOptions = Object.entries(MetalTypeEnum)
        .filter(([k]) => isNaN(Number(k)))
        .map(([label, value]) => ({ label, value }));

    // const goldPriceOptions: TDropdownOption[] = formik.values.metalType === 'Gold'
    //     ? [{ label: `RM${goldPrices.gbuy}`, value: goldPrices.gbuy }]
    //     : formik.values.metalType === 'Silver'
    //         ? [{ label: `RM${silverPrices?.sbuy}`, value: silverPrices?.sbuy ?? '' }]
    //         : [];

    return (
        <form
            onSubmit={formik.handleSubmit}
            className={FormStyles.container}
            onKeyDown={blockEnterKey}
        >
            <div className={FormStyles['filter-container']}>
                <LabelledDropdown
                    isFilter
                    label='Search for customer name or ID* : '
                    options={customerOptions}
                    value={name}
                    onChange={handleOnSearchCustomer}
                    onSelectOption={handleOnSelectCustomer}
                    noOptionsMessage={!name ? 'Search for customer name or ID' : 'No customer found'}
                    isLoading={gettingCustomers}
                    autoComplete='off'
                    optionSelected={!!customerOptions.find((option) => option.label === name)}
                    inputTestId='collateral-customer-name-input-dropdown'
                    optionTestId='collateral-customer-name-options-list-dropdown'
                />
            </div>

            <div className={FormStyles['form-container']}>
                <LabelledInput
                    label='Customer Name* :'
                    value={customerDetails.name}
                    disabled
                    className={FormStyles['span-2']}
                    data-testid='collateral-customer-name-input'
                />
            </div>

            <div className={FormStyles['form-container']}>
                <LabelledInput
                    label='ID Number* :'
                    value={customerDetails.idNo}
                    disabled
                />

                <LabelledInput
                    label='CIF No.* :'
                    value={customerDetails.CIFNo}
                    disabled
                />

                <LabelledDropdown
                    id='metalType'
                    name='metalType'
                    label='Metal Type *:'
                    options={metalTypeOptions}
                    value={formik.values.metalType}
                    error={formik.touched.metalType ? formik.errors.metalType : undefined}
                    onChange={handleOnSearchMetalType}
                    onSelectOption={handleOnSelectMetalType}
                    // onBlur={formik.handleBlur}
                    noOptionsMessage='No metal type found'
                    autoComplete='off'
                    optionSelected={!!metalTypeOptions.find((option) => option.label === formik.values.metalType)}
                />

                <div />

                <LabelledInput
                    id={selectedMetalType === MetalTypeEnum.Silver ? 'silverPrice' : 'goldPrice'}
                    name={selectedMetalType === MetalTypeEnum.Silver ? 'silverPrice' : 'goldPrice'}
                    label='Price (RM)* :'
                    value={selectedMetalType === MetalTypeEnum.Silver ? formik.values.silverPrice : formik.values.goldPrice}
                    error={
                        selectedMetalType === MetalTypeEnum.Silver
                            ? (formik.touched.silverPrice ? formik.errors.silverPrice : undefined)
                            : (formik.touched.goldPrice ? formik.errors.goldPrice : undefined)
                    }
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!formik.values.productId || useApi}
                />

                <div className={FormStyles['use-api-container']}>
                    <div className={FormStyles['flex-div']}>
                        <Checkbox
                            label=''
                            isChecked={useApi}
                            onChange={() => {
                                const fieldName = selectedMetalType === MetalTypeEnum.Silver ? 'silverPrice' : 'goldPrice';

                                if (useApi){
                                    setUseApi(false);
                                    formik.handleChange({ target: { name: fieldName, value: '' } });
                                    return;
                                }

                                setUseApi(!useApi);
                                const apiPrice = selectedMetalType === MetalTypeEnum.Silver
                                    ? (silverPrices?.gbuy ?? '')
                                    : goldPrices.gbuy;
                                formik.handleChange({ target: { name: fieldName, value: apiPrice } });
                            }}
                        />

                        <p>Use Api</p>
                    </div>

                    <div className={FormStyles['flex-div']}>
                        <p>{`Current ${formik.values.metalType || 'Gold'} Price: `}</p>
                        <p>{`RM${formik.values.metalType === 'Silver' ? (silverPrices?.gbuy ?? '-') : goldPrices.gbuy}`}</p>
                    </div>

                </div>

                <LabelledDropdown
                    inputTestId='collateral-productInput-dropdown'
                    optionTestId='collateral-productOptions-dropdown'
                    id='productName'
                    name='productName'
                    label='Product Name *: '
                    options={inventoryOptions().sort((a, b) => a.value - b.value)}
                    value={formik.values.productName}
                    error={formik.touched.productName ? formik.errors.productName : undefined}
                    onChange={handleOnSearchProduct}
                    onSelectOption={handleOnSelectProduct}
                    // onBlur={formik.handleBlur}
                    noOptionsMessage='No product found'
                    autoComplete='off'
                    optionSelected={!!inventoryOptions().find((option) => option.label === formik.values.productName)}
                    disabled={!formik.values.customerId
                            || customerInventory.length === 0}
                />

                <div className={FormStyles['available-balance']}>
                    <Text
                        label='Available balance:'
                    />

                    <Text
                        testId='collateral-available-balance-label'
                        label={customerBalance}
                        isBold
                    />
                </div>

                <LabelledInput
                    id='amount'
                    name='amount'
                    label='Collateral Amount (g)* :'
                    value={formik.values.amount}
                    error={formik.touched.amount ? formik.errors.amount : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!formik.values.productId}
                />

                <LabelledDatepicker
                    id='collateralEndDate'
                    name='collateralEndDate'
                    label='Collateral Expiry Date* :'
                    selectedDate={formik.values.collateralEndDate}
                    error={formik.touched.collateralEndDate ? formik.errors.collateralEndDate : undefined}
                    setSelectedDate={(date: string) => formik.setFieldValue('collateralEndDate', dayjs(date).toISOString())}
                    onBlur={formik.handleBlur}
                    minDate={dayjs().add(1, 'day').toISOString()}
                />

                <LabelledInput
                    label='Market Value* :'
                    value={marketValue?.toString() || ''}
                    disabled
                />

                <LabelledInput
                    id='remarks'
                    name='remarks'
                    label='Remarks :'
                    value={formik.values.remarks}
                    error={formik.touched.remarks ? formik.errors.remarks : undefined}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                />
            </div>

            <div className={`${FormStyles['button-container']} ${FormStyles['span-2']}`}>
                <div className={FormStyles['button-subcontainer']}>
                    <ActionButton
                        label={submitButtonLabel}
                        type='submit'
                        disabled={formik.isSubmitting || !formik.dirty || actionButtonIsLoading}
                    />
                </div>
            </div>
        </form>
    );
};

export default CustomerManagementCreateCollateralForm;
